import React, { FC, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { Alert, Button, Drawer, Empty, Input, Spin, Table, Tag } from 'antd';
import {
	CheckCircleOutlined,
	CloseCircleOutlined,
	FileTextOutlined,
	FilterOutlined,
	LinkOutlined,
	SyncOutlined,
} from '@ant-design/icons';
import { complianceDocumentsRestCrudThunks } from '../../thunks/compliance_documents_thunks';
import {
	linkComplianceDocumentToInstance,
	replaceComplianceDocumentOnInstance,
} from '../../thunks/compliance_request_instances_thunks';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	COMPLIANCE_EXPIRY_STATUSES,
	ComplianceDocument,
	ComplianceRequestInstance,
} from '../../types/ComplianceTypes';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { deriveComplianceExpiryStatus } from '../../utils/ComplianceUtils';
import {
	COMPLIANCE_EXPIRY_STATUS_COLORS,
	COMPLIANCE_EXPIRY_STATUS_DISPLAY_NAMES,
} from '../compliance_common/ComplianceConstants';
import AddComplianceDocumentModal from './AddComplianceDocumentModal';
import useMountEffect from '../../hooks/useMountEffect';

const CANDIDATES_TARGET_COLLECTION = 'linkDocumentCandidates';
const PAGE_SIZE = 10;

const formatDate = (isoDate) => (isoDate ? moment(isoDate).format('D MMM, YYYY') : '--');

// Icon per derived expiry status (documents have no server expiryStatus).
const EXPIRY_ICONS = {
	[COMPLIANCE_EXPIRY_STATUSES.UP_TO_DATE]: CheckCircleOutlined,
	[COMPLIANCE_EXPIRY_STATUSES.EXPIRING_SOON]: SyncOutlined,
	[COMPLIANCE_EXPIRY_STATUSES.EXPIRED]: CloseCircleOutlined,
};

interface ChooseComplianceDocumentModalProps {
	visible: boolean;
	// 'link' (default) links a document to an instance with no doc yet; 'replace'
	// swaps the currently-linked document for a different one.
	mode?: 'link' | 'replace';
	instance: ComplianceRequestInstance;
	onCancel: () => void;
	onSuccess: () => void;
	candidateDocuments: ComplianceDocument[];
	total: number;
	fetching: boolean;
	fetchErrors: string[];
	linking: boolean;
	fetchCandidates: (params: any, pagination: { current: number; pageSize: number }) => Promise<any>;
	linkDocument: (instanceId: number, complianceDocumentId: number) => Promise<any>;
	replaceDocument: (instanceId: number, complianceDocumentId: number) => Promise<any>;
}

const ChooseComplianceDocumentModal: FC<ChooseComplianceDocumentModalProps> = ({
	visible,
	mode = 'link',
	instance,
	onCancel,
	onSuccess,
	candidateDocuments,
	total,
	fetching,
	fetchErrors,
	linking,
	fetchCandidates,
	linkDocument,
	replaceDocument,
}): React.ReactElement => {
	const isReplaceMode = mode === 'replace';
	const [selectedDocumentId, setSelectedDocumentId] = useState(null);
	const [addDocumentModalOpen, setAddDocumentModalOpen] = useState(false);
	// When true, the request's compliance-type filter is cleared (all docs shown).
	const [typeFilterCleared, setTypeFilterCleared] = useState(false);
	const [searchText, setSearchText] = useState('');
	const [candidatePage, setCandidatePage] = useState(1);

	const request = nullSafeGetOrElse('complianceRequest', instance, {});
	const typeName = nullSafeGet('complianceType.name', request);
	const requestTypeId = nullSafeGet('complianceTypeId', request);

	// Server filter params derived from the current UI state. Undefined-valued keys
	// are omitted so we never send `search: undefined` / `complianceTypeId: undefined`.
	//
	// BACKEND FOLLOW-UP: this candidate list should also be scoped to the requesting
	// client (instance.buyerCompanyId) — a supplier shouldn't be offered documents
	// shared only with other clients. The /compliance/documents endpoint has no
	// buyerCompanyId filter param today, and filtering client-side would break the
	// server pagination, so add a buyerCompanyId (shared-with) filter to that endpoint
	// and pass it here. (The backend still rejects an invalid cross-client link at
	// submit, so this is a UX/hardening gap, not a data-exposure hole at write time.)
	const buildParams = () => {
		const params: any = {};
		if (request.countryCode) {
			params.countryCode = request.countryCode;
		}
		const trimmed = searchText.trim();
		if (trimmed) {
			params.search = trimmed;
		}
		if (!!requestTypeId && !typeFilterCleared) {
			params.complianceTypeId = requestTypeId;
		}
		return params;
	};

	// Clear any selection on a candidate reload — the previously-selected doc may not
	// be in the new result set, and a hidden selection must never be linked.
	const loadPage = (page) => {
		setSelectedDocumentId(null);
		return fetchCandidates(buildParams(), { current: page, pageSize: PAGE_SIZE });
	};

	useMountEffect(() => {
		loadPage(1);
	});

	// Debounce search so it fires once the user pauses, not per keystroke. The
	// `=== undefined ? null` guard skips the initial mount so it doesn't double-fetch
	// alongside the mount effect (mirrors AdvancedFilters).
	const searchTimer = useRef(undefined);
	useEffect(() => {
		clearTimeout(searchTimer.current);
		searchTimer.current =
			searchTimer.current === undefined
				? null
				: setTimeout(() => {
						setCandidatePage(1);
						loadPage(1);
				  }, 400);
	}, [searchText]);

	const openAddDocumentModal = () => setAddDocumentModalOpen(true);
	const closeAddDocumentModal = () => setAddDocumentModalOpen(false);

	const handleAddDocumentSuccess = () => {
		setAddDocumentModalOpen(false);
		setCandidatePage(1);
		loadPage(1);
	};

	const handleConfirm = () => {
		if (!selectedDocumentId) {
			return;
		}
		const action = isReplaceMode ? replaceDocument : linkDocument;
		action(instance.id, selectedDocumentId).then(onSuccess);
	};

	// The type filter narrows the server query to the request's compliance type.
	// When active, the server returns strictly that type (an intentional change from
	// the old client filter, which also surfaced untyped docs).
	const typeFilterActive = !!requestTypeId && !typeFilterCleared;

	// Toggling the type filter refetches page 1 with params computed from the intended
	// next state — state updates are async, so we can't read `typeFilterCleared` yet.
	const applyTypeFilterCleared = (next) => {
		setTypeFilterCleared(next);
		setCandidatePage(1);
		setSelectedDocumentId(null);
		const params: any = {};
		if (request.countryCode) {
			params.countryCode = request.countryCode;
		}
		const trimmed = searchText.trim();
		if (trimmed) {
			params.search = trimmed;
		}
		if (!!requestTypeId && !next) {
			params.complianceTypeId = requestTypeId;
		}
		fetchCandidates(params, { current: 1, pageSize: PAGE_SIZE });
	};

	// Derived expiry status + date(s) — mirrors the Documents tab. Documents carry
	// effectiveDate/expiryDate but no server expiryStatus, so it's derived client-side.
	const renderExpiration = (doc: ComplianceDocument) => {
		const effectiveDate = nullSafeGet('effectiveDate', doc);
		const expiryDate = nullSafeGet('expiryDate', doc);
		if (!effectiveDate && !expiryDate) {
			return <span className="text-character-secondary">--</span>;
		}
		const status = deriveComplianceExpiryStatus(expiryDate);
		const color = status ? COMPLIANCE_EXPIRY_STATUS_COLORS[status] : undefined;
		const IconComponent = status ? EXPIRY_ICONS[status] : null;
		return (
			<div className="flex flex-col gap-1">
				{status ? (
					<span
						className="footnoteregular inline-flex flex-row items-center gap-1"
						style={{ color }}
					>
						{IconComponent ? <IconComponent /> : null}
						{COMPLIANCE_EXPIRY_STATUS_DISPLAY_NAMES[status]}
					</span>
				) : null}
				{effectiveDate && expiryDate ? (
					<>
						<span className="footnoteregular text-character-secondary">
							Effective {formatDate(effectiveDate)}
						</span>
						<span className="footnoteregular text-character-secondary">
							Expiry: {formatDate(expiryDate)}
						</span>
					</>
				) : expiryDate ? (
					<span className="footnoteregular text-character-secondary">{formatDate(expiryDate)}</span>
				) : (
					<span className="footnoteregular text-character-secondary">
						Effective {formatDate(effectiveDate)}
					</span>
				)}
			</div>
		);
	};

	const renderName = (doc: ComplianceDocument) => {
		const docTypeName = nullSafeGet('complianceType.name', doc);
		return (
			<div className="flex flex-col gap-1">
				<span className="bodymedium">{doc.name}</span>
				{docTypeName ? (
					<span className="footnoteregular text-character-secondary">{docTypeName}</span>
				) : null}
			</div>
		);
	};

	const renderFileLink = (doc: ComplianceDocument) => {
		if (!doc.fileUrl) {
			return <span className="text-character-secondary">--</span>;
		}
		const fileLabel = nullSafeGetOrElse('fileName', doc, 'document');
		return (
			<a
				className="flex min-w-0 flex-row items-center gap-1"
				href={doc.fileUrl}
				target="_blank"
				rel="noopener noreferrer"
				title={fileLabel}
			>
				<FileTextOutlined className="shrink-0" />
				<span className="truncate">{fileLabel}</span>
			</a>
		);
	};

	// A columnar Table (like the Documents tab) rather than a CSS grid — antd sizes
	// columns + row height reliably, avoiding the flex/grid collapse the grid hit.
	const documentColumns = [
		{ title: 'Compliance Doc', key: 'name', width: '38%', render: (text, doc) => renderName(doc) },
		{
			title: 'Expiration Date',
			key: 'expiration',
			width: '32%',
			render: (text, doc) => renderExpiration(doc),
		},
		{ title: 'File', key: 'file', width: '30%', render: (text, doc) => renderFileLink(doc) },
	];

	const rowSelection = {
		type: 'radio' as const,
		selectedRowKeys: selectedDocumentId ? [selectedDocumentId] : [],
		onChange: (selectedRowKeys) => setSelectedDocumentId(selectedRowKeys[0]),
	};

	const renderBody = () => {
		if (fetching && candidateDocuments.length === 0) {
			return (
				<div className="flex justify-center py-12">
					<Spin />
				</div>
			);
		}
		if (fetchErrors.length > 0) {
			return (
				<Alert
					type="error"
					message="Couldn't load your compliance documents."
					description={fetchErrors.join(', ')}
				/>
			);
		}
		const emptyText = (
			<Empty
				image={Empty.PRESENTED_IMAGE_SIMPLE}
				description={`No matching compliance documents. Add one to ${
					isReplaceMode ? 'replace the linked document' : 'link it to this request'
				}.`}
			/>
		);
		return (
			<Table
				columns={documentColumns}
				dataSource={candidateDocuments}
				rowKey={(doc) => doc.id}
				rowSelection={rowSelection}
				pagination={{
					current: candidatePage,
					pageSize: PAGE_SIZE,
					total,
					showSizeChanger: false,
					onChange: (page) => {
						setCandidatePage(page);
						loadPage(page);
					},
				}}
				showHeader={true}
				locale={{ emptyText }}
				rowClassName="clickable-paginated-index-row"
				onRow={(doc) => ({ onClick: () => setSelectedDocumentId(doc.id) })}
			/>
		);
	};

	const footer = (
		<div className="flex flex-row justify-end gap-2">
			<Button onClick={onCancel}>Cancel</Button>
			<Button
				type="primary"
				icon={<LinkOutlined />}
				disabled={
					!selectedDocumentId || !candidateDocuments.some((doc) => doc.id === selectedDocumentId)
				}
				loading={linking}
				onClick={handleConfirm}
			>
				{isReplaceMode ? 'Replace Document' : 'Link Document'}
			</Button>
		</div>
	);

	return (
		<Drawer
			visible={visible}
			title={
				isReplaceMode
					? 'Choose a Compliance Document to replace'
					: 'Choose a Compliance Document to link'
			}
			onClose={onCancel}
			placement="right"
			width="50%"
			destroyOnClose={true}
			footer={footer}
		>
			<div className="mb-3 flex flex-row items-center gap-2">
				<Input.Search
					placeholder="input search text"
					value={searchText}
					onChange={(event) => setSearchText(event.target.value)}
					style={{ maxWidth: 280 }}
					allowClear={true}
				/>
				{requestTypeId ? (
					<Button
						type={typeFilterActive ? 'primary' : 'default'}
						icon={<FilterOutlined />}
						onClick={() => applyTypeFilterCleared(!typeFilterCleared)}
					/>
				) : null}
				<Button className="ml-auto" type="primary" ghost={true} onClick={openAddDocumentModal}>
					+ Add Document
				</Button>
			</div>
			{typeFilterActive && typeName ? (
				<div className="mb-3">
					<Tag closable={true} onClose={() => applyTypeFilterCleared(true)}>
						Compliance Type: {typeName}
					</Tag>
				</div>
			) : null}
			{renderBody()}
			{addDocumentModalOpen && (
				<AddComplianceDocumentModal
					visible={addDocumentModalOpen}
					onCancel={closeAddDocumentModal}
					onSuccess={handleAddDocumentSuccess}
				/>
			)}
		</Drawer>
	);
};

const mapStateToProps = (state) => ({
	candidateDocuments: getRecordsForTargetCollection(
		state.compliance_documents,
		CANDIDATES_TARGET_COLLECTION
	),
	total: nullSafeGetOrElse(
		`${CANDIDATES_TARGET_COLLECTION}.pagination.total`,
		state.compliance_documents,
		0
	),
	fetching: state.compliance_documents.fetching,
	fetchErrors: state.compliance_documents.fetchErrors,
	linking: state.compliance_request_instances.updating,
});

const mapDispatchToProps = (dispatch) => ({
	fetchCandidates: (params, pagination) =>
		dispatch(
			complianceDocumentsRestCrudThunks(ROLE_TYPES.SUPPLIER).read(
				params,
				CANDIDATES_TARGET_COLLECTION,
				pagination
			)
		),
	linkDocument: (instanceId, complianceDocumentId) =>
		dispatch(
			linkComplianceDocumentToInstance(ROLE_TYPES.SUPPLIER)(instanceId, { complianceDocumentId })
		),
	replaceDocument: (instanceId, complianceDocumentId) =>
		dispatch(
			replaceComplianceDocumentOnInstance(ROLE_TYPES.SUPPLIER)(instanceId, { complianceDocumentId })
		),
});

export default connect(mapStateToProps, mapDispatchToProps)(ChooseComplianceDocumentModal);
